import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import { requests } from '../../../core/ajax'
import { feedback } from '../../../core/feedback'
import { PROCESSING_STATES } from '../constants'

export const useGasAppCalls = () => {
  const { formatMessage } = useIntl()
  const { getById, post, putById, put, deleteCall } = requests

  const noResponseError = () =>
    feedback('error', formatMessage({ id: 'calls.responseless.error.description' }))
  const defaultError = () =>
    feedback('error', formatMessage({ id: 'calls.default.error.description' }))

  const getGasApps = useCallback(
    (userId) =>
      new Promise((resolve, reject) => {
        getById('/solicitudes/by_usuario', userId)
          .then(({ data }) =>
            // GAS_APPS_ITEMS
            data
              .filter(
                (application) =>
                  // !FILTERED_STATES[application['estado']] &&
                  //Workaround to prevent applications without camino_solicitud or with incoherences
                  application['camino_solicitud'] &&
                  ((application['camino_solicitud'] === '01' &&
                    PROCESSING_STATES[application.estado]) ||
                    (application['camino_solicitud'] === '01' &&
                      !PROCESSING_STATES[application.estado]) ||
                    application['camino_solicitud'] !== '01')
              )
              .sort((a, b) => new Date(b['fecha_solicitud']) - new Date(a['fecha_solicitud']))
          )
          .then((cleanData) => resolve(cleanData))
          .catch((error) => {
            return reject(error.response)
          })
      }),
    [] // eslint-disable-line
  )

  const getGasApp = useCallback(
    (id) =>
      new Promise((resolve, reject) => {
        getById('/solicitudes', id)
          .then(({ data }) =>
            // GAS_APPS_ITEMS
            resolve(data)
          )
          .catch((error) => {
            return reject(error.response)
          })
      }),
    [] // eslint-disable-line
  )

  const surveyPost = useCallback(
    (id) =>
      new Promise((resolve, reject) => {
        post('/encuesta', { solicitud_cidi_serial_id: id })
          .then((response) => resolve(response))
          .catch((response) => {
            if (!response) {
              noResponseError()
              return reject('No response')
            }
            switch (response.status) {
              case '403': //Permisson denied by role
                return reject(response)
              default:
                defaultError()
            }
            return reject(response)
          })
      }),
    [] // eslint-disable-line
  )

  const cancelPut = useCallback(
    (id, payload) =>
      new Promise((resolve, reject) => {
        putById('/solicitudes/anular_solicitud', id, payload)
          .then((response) => resolve(response))
          .catch((response) => {
            if (!response) {
              noResponseError()
              return reject('No response')
            }
            switch (response.status) {
              case '403': //Permisson denied by role
                return reject(response)
              default:
                defaultError()
            }
            return reject(response)
          })
      }),
    [] // eslint-disable-line
  )

  const rollbackPut = useCallback(
    (id, payload) =>
      new Promise((resolve, reject) => {
        putById('/solicitudes', id, payload)
          .then((response) => resolve(response))
          .catch((response) => {
            if (!response) {
              noResponseError()
              return reject('No response')
            }
            switch (response.status) {
              case '403': //Permisson denied by role
                return reject(response)
              default:
                defaultError()
            }
            return reject(response)
          })
      }),
    [] // eslint-disable-line
  )

  const uploadDocument = useCallback(
    (payload) =>
      new Promise((resolve, reject) => {
        post('/documento', payload)
          .then((response) => resolve(response))
          .catch((response) => {
            if (!response) {
              noResponseError()
              return reject('No response')
            }
            switch (response.status) {
              case '403': //Permisson denied by role
                return reject(response)
              default:
                defaultError()
            }
            return reject(response)
          })
      }),
    [] // eslint-disable-line
  )

  const getInstaller = useCallback(
    (payload) =>
      new Promise((resolve, reject) => {
        post('/solicitudes/asociar_instalador', payload)
          .then((response) => {
            feedback('success', formatMessage({ id: 'global.success.get_installer.title' }))
            resolve(response)
          })
          .catch(({ response }) => {
            if (!response) {
              noResponseError()
              return reject('No response')
            }
            switch (response.status) {
              case 400:
                if (response.data?.code === 4009) {
                  feedback(
                    'error',
                    formatMessage({ id: `global.errors.handled.code.${response.data.code}` })
                  )
                  return reject(response)
                }
                defaultError()
                break
              case 500:
                if (response.data?.code === 8001) {
                  feedback(
                    'error',
                    formatMessage({ id: `global.errors.handled.code.${response.data.code}` })
                  )
                  return reject(response)
                }
                defaultError()
                break
              default:
                defaultError()
            }
            return reject(response)
          })
      }),
    [] // eslint-disable-line
  )

  const reviewCall = useCallback(
    (id, payload) =>
      new Promise((resolve, reject) => {
        put(`/solicitudes/cambiar_estado/${id}/00`, payload)
          .then((response) => resolve(response))
          .catch((response) => {
            if (!response) {
              noResponseError()
              return reject('No response')
            }
            switch (response.status) {
              case '403': //Permisson denied by role
                return reject(response)
              default:
                defaultError()
            }
            return reject(response)
          })
      }),
    [] // eslint-disable-line
  )

  const cancelInstaller = useCallback(
    (id, payload) =>
      new Promise((resolve, reject) => {
        put(`solicitudes/asociar_instalador/${id}`, payload)
          .then((response) => {
            resolve(response)
            feedback('success', 'El instalador se ha desasignado correctamente')
          })
          .catch((response) => {
            if (!response) {
              noResponseError()
              return reject('No response')
            }
            switch (response.status) {
              case '403':
                return reject(response)
              case '400':
                if (response?.data?.code === 4009) {
                  feedback(
                    'error',
                    formatMessage({ id: `global.errors.handled.code.${response.data.code}` })
                  )
                } else {
                  defaultError()
                }
              case '500':
                if (response?.data?.code === 8001) {
                  feedback(
                    'error',
                    formatMessage({ id: `global.errors.handled.code.${response.data.code}` })
                  )
                } else {
                  defaultError()
                }

              default:
                defaultError()
            }
            return reject(response)
          })
      }),
    []
  )

  const getReview = useCallback(
    (id) =>
      new Promise((resolve, reject) => {
        getById('/valoraciones', id)
          .then(({ data }) => resolve(data))
          .catch((error) => {
            return reject(error.response)
          })
      }),
    [] // eslint-disable-line
  )

  return {
    getInstaller,
    getGasApps,
    rollbackPut,
    getGasApp,
    surveyPost,
    cancelPut,
    uploadDocument,
    reviewCall,
    getReview,
    cancelInstaller,
  }
}
